import Joi from 'joi';
import countries from '../../../Lib/Constants/countries';
import makeYearsOptions from '../../../Lib/Utils/Select/make-years-options';
import divisionsOptions from '../../../Lib/Constants/divisions-options';

export default {
  pageTitle: 'Business Profile',
  cards: [
    {
      title: 'Company Details',
      fields: [
        {
          label: 'What year was your business established?',
          name: 'yearBusinessEstablished',
          type: 'select',
          options: makeYearsOptions({ numberOfYears: 100 }),
          required: true,
          specialValidation: Joi.number()
            .required()
            .max(new Date().getFullYear()),
        },
        {
          label: 'Do you have a direct-to-consumer business?',
          name: 'directToConsumerBusiness',
          type: 'yes_no',
          required: true,
        },
        {
          label: 'What is your company structure?',
          name: 'companyStructure',
          type: 'select',
          required: true,
          options: [
            {
              label: 'Public Entity',
              value: 'publicEntity',
            },
            {
              label: 'Privately Held',
              value: 'privatelyHeld',
            },
            {
              label: 'Wholly Owned',
              value: 'whollyOwned',
            },
            {
              label: 'Other',
              value: 'other',
            },
          ],
        },
        {
          cond: ({ companyStructure }) => companyStructure === 'other',
          label: 'Other, please describe',
          name: 'companyStructureOther',
          type: 'text',
          required: true,
        },
      ],
    },
    {
      title: 'Revenue',
      fields: [
        {
          label:
            'What is your annual revenue for the last fiscal/calendar year?',
          name: 'lastFiscalYear',
          type: 'select',
          required: true,
          options: [
            {
              label: '< $1 million USD',
              value: '< $1 million USD',
            },
            {
              label: '$1 – 5 million USD',
              value: '$1 – 5 million USD',
            },
            {
              label: '$6 – 10 million USD',
              value: '$6 – 10 million USD',
            },
            {
              label: '$10 - $50 million USD',
              value: '$10 - $50 million USD',
            },
            {
              label: '> $50 million USD',
              value: '> $50 million USD',
            },
          ],
        },
        {
          label:
            'What is your estimated volume or units sold for the last 12 months (LTM) in million ?',
          name: 'lastTwelveMonths',
          type: 'text',
          required: true,
          suffix: 'million',
          specialValidation: Joi.number().min(0),
        },
      ],
    },
    {
      title:
        'What other brands/clients do you work with? Please list your top 5.',
      fields: [
        {
          label: 'Brand/Client 1',
          name: 'brandClient1',
          type: 'text',
          required: true,
        },
        {
          label: 'Brand/Client 2',
          name: 'brandClient2',
          type: 'text',
          required: true,
        },
        {
          label: 'Brand/Client 3',
          name: 'brandClient3',
          type: 'text',
          required: true,
        },
        {
          label: 'Brand/Client 4',
          name: 'brandClient4',
          type: 'text',
          required: true,
        },
        {
          label: 'Brand/Client 5',
          name: 'brandClient5',
          type: 'text',
          required: true,
        },
      ],
    },
    {
      title: 'Categories',
      fields: [
        {
          label: 'What division/categories can you supply for?',
          name: 'divisionCategories',
          type: 'checkbox',
          required: true,
          options: divisionsOptions,
        },
        {
          label: 'What type of product are you making (check all that apply)?',
          name: 'productType',
          type: 'checkbox',
          required: true,
          options: [
            {
              label: 'Apparel',
              value: 'apparel',
            },
            {
              label: 'Shoes',
              value: 'shoes',
            },
            {
              label: 'Jewelry',
              value: 'jewelry',
            },
            {
              label: 'Eyewear',
              value: 'eyewear',
            },
            {
              label: 'Beauty',
              value: 'beauty',
            },
            {
              label: 'Other accessories',
              value: 'otherAccessories',
            },
          ],
        },
        {
          label: 'What size range do you work with? (Check all that apply)',
          name: 'sizeRange',
          type: 'checkbox',
          required: true,
          options: [
            {
              label: 'Women’s',
              value: 'women',
            },
            {
              label: 'Women’s Plus',
              value: 'womenPlus',
            },
            {
              label: 'Kid’s (Girls & Boys)',
              value: 'kids',
            },
            {
              label: 'Men’s',
              value: 'mens',
            },
          ],
        },
      ],
    },
    {
      title: 'MOQs',
      fields: [
        {
          label: 'What are your MOQs per style/color?',
          name: 'moqs',
          type: 'text',
          required: true,
          specialValidation: Joi.number().required().min(0),
        },
      ],
    },
    {
      title: 'Production capacity, volume & lead time',
      fields: [
        {
          cond: ({ divisionCategories }) =>
            divisionCategories.includes('Women'),
          label:
            'Monthly Production Capacity for Women Division * (% of your production)',
          name: 'monthlyProductionCapacityWomen',
          type: 'text',
          required: true,
          suffix: '%',
          specialValidation: Joi.number().min(0).max(100),
        },
        {
          cond: ({ divisionCategories }) => divisionCategories.includes('Men'),
          label:
            'Monthly Production Capacity for Men Division * (% of your production)',
          name: 'monthlyProductionCapacityMen',
          type: 'text',
          required: true,
          suffix: '%',
          specialValidation: Joi.number().min(0).max(100),
        },
        {
          cond: ({ divisionCategories }) => divisionCategories.includes('Kids'),
          label:
            'Monthly Production Capacity for Kids Division * (% of your production)',
          name: 'monthlyProductionCapacityKids',
          type: 'text',
          required: true,
          suffix: '%',
          specialValidation: Joi.number().min(0).max(100),
        },
        {
          cond: ({ divisionCategories }) =>
            divisionCategories.includes('Beauty'),
          label:
            'Monthly Production Capacity for Beauty Division * (% of your production)',
          name: 'monthlyProductionCapacityBeauty',
          type: 'text',
          required: true,
          suffix: '%',
          specialValidation: Joi.number().min(0).max(100),
        },
        {
          label: 'What is your sample monthly Development capacity?',
          name: 'monthlyDevelopmentSampleCapacity',
          type: 'text',
          required: true,
          specialValidation: Joi.number().min(0),
        },
        {
          label: 'What is your Development sample lead-time? (in weeks)',
          name: 'developmentSampleLeadTime',
          type: 'text',
          required: true,
          specialValidation: Joi.number().min(0),
        },
        {
          label: 'What is your average production lead-time? (in weeks)',
          name: 'averageProductionLeadTime',
          type: 'text',
          required: true,
          specialValidation: Joi.number().min(0),
        },
      ],
    },
    {
      title: 'Design & Brands',
      fields: [
        {
          label: 'Do you have a designed line?',
          name: 'designedLine',
          type: 'yes_no',
          required: true,
        },
        {
          cond: ({ designedLine }) => designedLine === 'yes',
          label: 'Would you please send us your line sheets?',
          name: 'lineSheets',
          type: 'yes_no',
          required: true,
        },
        {
          cond: ({ lineSheets }) => lineSheets === 'yes',
          label: 'Please provide the file',
          name: 'lineSheetsUpload',
          type: 'file',
          required: true,
        },
        {
          label: 'Do you have your own private label/brand?',
          name: 'ownBrand',
          type: 'yes_no',
          required: true,
        },
        {
          cond: ({ ownBrand }) => ownBrand === 'yes',
          label: 'Label/Brand Name',
          name: 'labelBrandName',
          type: 'text',
          required: true,
        },
        {
          label: 'Do you currently have any Licensed Properties?',
          name: 'licenseAgreement',
          type: 'yes_no',
          required: true,
        },
        {
          cond: ({ licenseAgreement }) => licenseAgreement === 'yes',
          label: 'Please provide a list of license property names',
          name: 'licenses',
          type: 'text',
          required: true,
        },
        {
          label: 'Do you have Design Capabilities?',
          name: 'designCapabilities',
          type: 'yes_no',
          required: true,
        },
        {
          cond: ({ designCapabilities }) => designCapabilities === 'yes',
          label: 'If yes, # of designers?',
          name: 'numberOfDesigners',
          type: 'text',
          required: true,
          specialValidation: Joi.number().min(0),
        },
        {
          label: 'Do you have a RN#?',
          name: 'rnNumber',
          type: 'yes_no',
          required: true,
        },
        {
          cond: ({ rnNumber }) => rnNumber === 'yes',
          label: 'RN#',
          name: 'rn',
          type: 'text',
          required: true,
          specialValidation: Joi.number().min(0),
        },
        {
          label:
            'Do you have the capability to add Fashion Nova Trim to your stock goods?',
          name: 'fnTrim',
          type: 'yes_no',
          required: true,
        },
        {
          label: 'Can you manufacture Fashion Nova exclusive styles?',
          name: 'fnExclusiveStyles',
          type: 'yes_no',
          required: true,
        },
      ],
    },
    {
      title: 'Terms',
      fields: [
        {
          label: 'Can you work with Net 30 days payment terms?',
          name: 'net30DaysPaymentTerms',
          type: 'yes_no',
          required: true,
        },
        {
          label:
            'Can you work with Incoterms (Delivery Duty Paid - To the Door)?\n' +
            'Delivery Address: FN Logistics, 12588 Florence Ave, Santa Fe Springs, CA 90670, United States',
          name: 'ddpIncoterms',
          type: 'yes_no',
          required: true,
        },
      ],
    },
    {
      title: 'Manufacturing locations',
      fields: [
        {
          label: 'Where are you manufacturing? COO (County of Origin)?',
          name: 'manufacturing',
          type: 'select',
          required: true,
          multiple: true,
          options: countries.map((country) => ({
            value: country.value,
            label: country.text,
          })),
        },
      ],
    },
    {
      title: 'Factory Ownership',
      fields: [
        {
          label:
            'If you own, or have any ownership interest, in any of the factories you work with, you must add them here.',
          name: 'factoriesOwnership',
          min: 0,
          type: 'subForm',
          idKey: 'factoryDetailsName',
          buttonText: 'Add a factory',
          subForm: {
            pageTitle: 'Add Factory Ownership',
            cards: [
              {
                title: 'Factory Ownership',
                fields: [
                  {
                    label: 'Provide name of factory.',
                    name: 'factoryDetailsName',
                    type: 'text',
                    required: true,
                  },
                  {
                    label: 'What country is it located in?',
                    name: 'factoryDetailsCountry',
                    type: 'select',
                    required: true,
                    options: countries.map((country) => ({
                      value: country.value,
                      label: country.text,
                    })),
                  },
                ],
              },
            ],
          },
        },
      ],
    },
  ],
};
